import { Card } from '@/globalComponents/card';
import DeviceBadge from '@/globalComponents/deviceBadge';
import ImageWithFallback from '@/globalComponents/image';
import { AssetsData } from '@/types/asset';
import ProductUtils from '@/utils/productUtils';
import styles from './style.module.scss';
import LocationIcon from '../../../public/images/icons/location.svg';
import { getAddress } from '@/utils/address';
import Image from '@/shared/Image';

export interface ProductDetailsProps extends AssetsData {}

const renderDeviceName = (title: string) => {
  return title.length > 100 ? `${title.slice(0, 100)}...` : title;
};

const renderInfoItem = (label: string, value: string) => {
  return (
    <p className={styles.infoTitle} data-testid={`${label.toLocaleLowerCase()}-label`}>
      {label}
      <span
        className={`block ${styles.infoValue}`}
        data-testid={`${label.toLocaleLowerCase()}-value`}
      >
        {value}
      </span>
    </p>
  );
};

export const ProductDetails = (props: ProductDetailsProps) => {
  const {
    img: image,
    name: title,
    address,
    planType,
    serialNumber,
    componentId,
    customerCode,
  } = props;

  const renderInfoSection = () => {
    return (
      <>
        {address && (
          <div className="flex items-center md:pb-[33px] pb-5 md:gap-[0.875rem] gap-s4 md:pt-0 pt-[22px] md:max-w-[70%]">
            <ImageWithFallback
              src={LocationIcon}
              alt="location-icon"
              className="self-start md:w-6 w-4 md:h-6 md:mt-[4px]"
            />
            <p className={`${styles.location}`} data-testid="product-location">
              {getAddress(address)}
            </p>
          </div>
        )}
        <div className={styles.info}>
          <div className={styles.firstRow}>
            {customerCode && renderInfoItem('Customer ID', customerCode)}
          </div>
          {componentId && renderInfoItem('Component ID', componentId)}
          {serialNumber && renderInfoItem('Serial No', serialNumber)}
        </div>
      </>
    );
  };

  const renderTitleSection = () => {
    const productLabel = new ProductUtils(props).getProductLabel();
    const deviceStatus = productLabel === 'AMC Tier' ? planType : productLabel;

    return (
      <div className="flex flex-col md:flex-row md:justify-between w-full md:pb-[0.75rem]">
        <p data-testid="product-title" className={`${styles.title} md:mb-0 mb-[7px]`}>
          {renderDeviceName(title)}
        </p>
        <DeviceBadge status={deviceStatus} tierNameClasses={styles.badgeText} />
      </div>
    );
  };

  return (
    <>
      <Card
        data-testid="product-details-container"
        classNames={`flex flex-row justify-between items-center md:mt-0 -mt-3 ${styles.card} w-full`}
      >
        <div className="flex md:flex-row flex-col items-center gap-s3 md:gap-5 w-full">
          <div className="md:flex md:flex-row hidden w-full">
            <div className="md:pl-[26px] md:pt-5 h-[7.3125rem] w-[8.375rem] self-start">
              <Image
                loading="eager"
                src={image}
                alt={title}
                data-testid="product-image"
                width={134}
                height={117}
                className={`h-full w-full lg:h-[110px] lg:w-[95px] md:self-center`}
              />
            </div>
            <div className="flex flex-col items-start h-full self-start w-full md:p-6">
              {renderTitleSection()}
              {renderInfoSection()}
            </div>
          </div>

          <div className="md:hidden block pr-4 pb-4 pl-4">
            <div className="flex w-full">
              <Image
                loading="eager"
                src={image}
                alt={title}
                width={64}
                height={78}
                data-testid="product-image"
                className="w-16 h-[78px] self-start mt-3 mr-2 -ml-2"
              />
              <div className="pt-[14px]">{renderTitleSection()}</div>
            </div>
            {renderInfoSection()}
          </div>
        </div>
      </Card>
    </>
  );
};
